class BillingForm {
  constructor(options = {}) {
    this.config = {
      formSelector: '#billingForm',
      cartFormSelector: 'form[action*="/cart"]',
      billingSectionSelector: '#billingSection',
      regimenFiscalSelector: '#regimenFiscal',
      usoCfdiSelector: '#usoCfdi',
      rfcSelector: '#rfc',
      rfcMessageSelector: '#rfcMessage',
      privacyCheckboxSelector: '#Privacidad',
      checkoutButtonSelector: '#update_cart_button',
      saveIndicatorSelector: '#saveIndicator',
      saveIndicatorTextSelector: '#saveIndicatorText',
      ...options
    };

    this.isSubmitting = false;

    this.boundHandleRadioClick = this.handleRadioClick.bind(this);
    this.boundHandleRegimenChange = this.handleRegimenFiscalChange.bind(this);
    this.boundHandleUsoCfdiChange = this.handleUsoCfdiChange.bind(this);
    this.boundHandleRFCInput = this.handleRFCInput.bind(this);
    this.boundHandleRFCBlur = this.handleRFCBlur.bind(this);
    this.boundHandlePrivacyChange = this.handlePrivacyCheckboxChange.bind(this);
    this.boundHandleCartFormSubmit = this.handleCartFormSubmit.bind(this);

    this.init();
  }

  init() {
    this.findElements();

    if (!this.elementsExist()) {
      console.warn('BillingForm: no se encontraron elementos requeridos');
      return;
    }

    this.removeEventListeners();
    this.setupEventListeners();
    this.updateButtonState();

    console.log('BillingForm inicializado correctamente');
  }

  findElements() {
    this.form = document.querySelector(this.config.formSelector);
    this.cartForm = document.querySelector(this.config.cartFormSelector);
    this.billingSection = document.querySelector(this.config.billingSectionSelector);
    this.regimenFiscal = document.querySelector(this.config.regimenFiscalSelector);
    this.usoCfdi = document.querySelector(this.config.usoCfdiSelector);
    this.rfc = document.querySelector(this.config.rfcSelector);
    this.rfcMessage = document.querySelector(this.config.rfcMessageSelector);
    this.privacyCheckbox = document.querySelector(this.config.privacyCheckboxSelector);
    this.checkoutButton = document.querySelector(this.config.checkoutButtonSelector);
    this.saveIndicator = document.querySelector(this.config.saveIndicatorSelector);
    this.saveIndicatorText = document.querySelector(this.config.saveIndicatorTextSelector);
  }

  elementsExist() {
    return !!(
      this.form &&
      this.billingSection &&
      this.regimenFiscal &&
      this.usoCfdi &&
      this.rfc &&
      this.checkoutButton &&
      this.cartForm
    );
  }

  setupEventListeners() {
    document.addEventListener('click', this.boundHandleRadioClick);
    this.regimenFiscal.addEventListener('change', this.boundHandleRegimenChange);
    this.usoCfdi.addEventListener('change', this.boundHandleUsoCfdiChange);
    this.rfc.addEventListener('input', this.boundHandleRFCInput);
    this.rfc.addEventListener('blur', this.boundHandleRFCBlur);

    if (this.privacyCheckbox) {
      this.privacyCheckbox.addEventListener('change', this.boundHandlePrivacyChange);
    }

    this.cartForm.addEventListener('submit', this.boundHandleCartFormSubmit);
  }

  removeEventListeners() {
    document.removeEventListener('click', this.boundHandleRadioClick);

    if (this.regimenFiscal) {
      this.regimenFiscal.removeEventListener('change', this.boundHandleRegimenChange);
    }

    if (this.usoCfdi) {
      this.usoCfdi.removeEventListener('change', this.boundHandleUsoCfdiChange);
    }

    if (this.rfc) {
      this.rfc.removeEventListener('input', this.boundHandleRFCInput);
      this.rfc.removeEventListener('blur', this.boundHandleRFCBlur);
    }

    if (this.privacyCheckbox) {
      this.privacyCheckbox.removeEventListener('change', this.boundHandlePrivacyChange);
    }

    if (this.cartForm) {
      this.cartForm.removeEventListener('submit', this.boundHandleCartFormSubmit);
    }
  }

  async handleRadioClick(event) {
    const radioOption = event.target.closest('.radio-option');
    if (!radioOption || !this.form.contains(radioOption)) return;

    const value = radioOption.dataset.value;
    const radioInput = radioOption.querySelector('input[type="radio"]');
    if (!radioInput) return;

    this.form.querySelectorAll('.radio-option').forEach(opt => {
      opt.classList.remove('selected');
    });

    radioOption.classList.add('selected');
    radioInput.checked = true;

    await this.handleBillingRequirement(value);

    setTimeout(() => {
      this.updateButtonState();
    }, 0);
  }

  async handleBillingRequirement(value) {
    if (value === 'Si') {
      this.billingSection.style.display = 'block';
      this.billingSection.classList.add('enabled');
      this.regimenFiscal.disabled = false;
    } else {
      this.billingSection.style.display = 'none';
      this.billingSection.classList.remove('enabled');
      this.resetBillingFields();
      await this.clearBillingAttributes();
    }

    this.updateButtonState();
  }

  handleRegimenFiscalChange() {
    if (this.regimenFiscal.value) {
      this.usoCfdi.disabled = false;
      this.usoCfdi.classList.remove('error');
    } else {
      this.usoCfdi.disabled = true;
      this.usoCfdi.value = '';
      this.rfc.disabled = true;
      this.rfc.value = '';
      this.clearRFCValidation();
    }

    this.updateButtonState();
  }

  handleUsoCfdiChange() {
    if (this.usoCfdi.value) {
      this.rfc.disabled = false;
      this.rfc.classList.remove('error');
      this.showRFCMessage('info', 'Ingrese un RFC válido de 12 o 13 caracteres');
    } else {
      this.rfc.disabled = true;
      this.rfc.value = '';
      this.clearRFCValidation();
    }

    this.updateButtonState();
  }

  handleRFCInput() {
    this.validateRFC(false);
    this.updateButtonState();
  }

  handleRFCBlur() {
    this.validateRFC(true);
    this.updateButtonState();
  }

  handlePrivacyCheckboxChange() {
    this.updateButtonState();
  }

  validateRFC(showMessage = true) {
    if (!this.rfc) return false;

    const rfcValue = this.rfc.value.toUpperCase().trim();
    this.rfc.value = rfcValue;

    this.rfc.classList.remove('error', 'success');
    this.clearRFCValidation(false);

    if (!rfcValue) {
      return false;
    }

    let isValid = false;

    try {
      if (typeof validateRfc === 'function') {
        const result = validateRfc(rfcValue, { omitVerificationDigit: true });
        isValid = !!result?.isValid;
      } else {
        const regexPersonaMoral = /^[A-Z&Ñ]{3}\d{6}[A-Z0-9]{3}$/;
        const regexPersonaFisica = /^[A-ZÑ&]{4}\d{6}[A-Z0-9]{3}$/;
        isValid = regexPersonaMoral.test(rfcValue) || regexPersonaFisica.test(rfcValue);
      }
    } catch (e) {
      isValid = false;
    }

    if (isValid) {
      this.rfc.classList.add('success');
      if (showMessage) this.showRFCMessage('success', '✓ RFC válido');
      return true;
    }

    this.rfc.classList.add('error');
    if (showMessage) this.showRFCMessage('error', '✗ RFC inválido. Verifique el formato');
    return false;
  }

  showRFCMessage(type, message) {
    if (!this.rfcMessage) return;
    this.rfcMessage.className = `form-message ${type}`;
    this.rfcMessage.textContent = message;
    this.rfcMessage.style.display = 'flex';
  }

  clearRFCValidation(hideMessage = true) {
    if (!this.rfc || !this.rfcMessage) return;
    this.rfc.classList.remove('error', 'success');

    if (hideMessage) {
      this.rfcMessage.style.display = 'none';
      this.rfcMessage.textContent = '';
      this.rfcMessage.className = 'form-message';
    }
  }

  resetBillingFields() {
    this.regimenFiscal.disabled = true;
    this.regimenFiscal.value = '';

    this.usoCfdi.disabled = true;
    this.usoCfdi.value = '';

    this.rfc.disabled = true;
    this.rfc.value = '';

    this.clearRFCValidation();

    [this.regimenFiscal, this.usoCfdi, this.rfc].forEach(field => {
      field.classList.remove('error', 'success');
    });
  }

  getSelectedBillingElement() {
    return this.form?.querySelector('input[Factura]:checked') || null;
  }

  getFormData() {
    const selected = this.getSelectedBillingElement();
    if (!selected) return null;

    const facturaValue = selected.getAttribute('Factura');

    const data = {
      requiresBilling: facturaValue,
      privacyAccepted: !!this.privacyCheckbox?.checked
    };

    if (facturaValue === 'Si') {
      data.regimenFiscal = this.regimenFiscal?.value || '';
      data.usoCfdi = this.usoCfdi?.value || '';
      data.rfc = this.rfc?.value?.toUpperCase().trim() || '';
      data.regimenFiscalTexto = this.regimenFiscal?.options[this.regimenFiscal.selectedIndex]?.text || '';
      data.usoCfdiTexto = this.usoCfdi?.options[this.usoCfdi.selectedIndex]?.text || '';
    }

    return data;
  }

  isFormValid() {
    const data = this.getFormData();
    if (!data) return false;

    if (data.requiresBilling === 'No') {
      return true;
    }

    if (data.requiresBilling === 'Si') {
      const rfcValid = this.validateRFC(false);

      return !!(
        data.regimenFiscal &&
        data.usoCfdi &&
        data.rfc &&
        rfcValid
      );
    }

    return false;
  }

  updateButtonState() {
    if (!this.checkoutButton) return;

    const isValid = this.isFormValid();

    this.checkoutButton.disabled = !isValid;

    if (isValid) {
      this.checkoutButton.removeAttribute('disabled');
    } else {
      this.checkoutButton.setAttribute('disabled', 'disabled');
    }
  }

  ensureCartAttributeInput(name, value) {
    if (!this.cartForm) return;

    let input = this.cartForm.querySelector(`input[name="attributes[${name}]"]`);

    if (!input) {
      input = document.createElement('input');
      input.type = 'hidden';
      input.name = `attributes[${name}]`;
      input.setAttribute('data-billing-attribute', 'true');
      this.cartForm.appendChild(input);
    }

    input.value = value ?? '';
  }

  injectCartAttributes() {
    if (!this.cartForm) return;

    this.cartForm.querySelectorAll('input[data-billing-attribute="true"]').forEach(el => el.remove());

    const data = this.getFormData();
    if (!data) return;

    this.ensureCartAttributeInput('Factura', data.requiresBilling || 'No');
    this.ensureCartAttributeInput('Privacidad_Aceptada', data.privacyAccepted ? 'Si' : 'No');

    if (data.requiresBilling === 'No') {
      this.ensureCartAttributeInput('CFDI', null);
      this.ensureCartAttributeInput('USO_CFDI', null);
      this.ensureCartAttributeInput('RFC', null);
      this.ensureCartAttributeInput('Regimen_Fiscal_Texto', null);
      this.ensureCartAttributeInput('Uso_CFDI_Texto', null);
    }

    if (data.requiresBilling === 'Si') {
      this.ensureCartAttributeInput('CFDI', data.regimenFiscal || '');
      this.ensureCartAttributeInput('USO_CFDI', data.usoCfdi || '');
      this.ensureCartAttributeInput('RFC', data.rfc || '');
      this.ensureCartAttributeInput('Regimen_Fiscal_Texto', data.regimenFiscalTexto || '');
      this.ensureCartAttributeInput('Uso_CFDI_Texto', data.usoCfdiTexto || '');
    }

    
  }

  async clearBillingAttributes() {
    // 1) Quitar hidden inputs del form actual
    if (this.cartForm) {
      const billingKeys = [
        'CFDI',
        'USO_CFDI',
        'RFC',
        'Regimen_Fiscal_Texto',
        'Uso_CFDI_Texto'
      ];

      billingKeys.forEach((name) => {
        const input = this.cartForm.querySelector(`input[data-billing-attribute="true"][name="attributes[${name}]"]`);
        if (input) input.remove();
      });
    }

    // 2) Limpiar también los atributos ya persistidos en el carrito Shopify
    try {
      const response = await fetch('/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          attributes: {
            CFDI: '',
            USO_CFDI: '',
            RFC: '',
            Regimen_Fiscal_Texto: '',
            Uso_CFDI_Texto: '',
            addressName: '',
            addressNumber: ''
          }
        })
      });

      if (!response.ok) {
        throw new Error(`Error HTTP ${response.status}`);
      }

      const cart = await response.json();
      console.log('Atributos fiscales limpiados del carrito:', cart.attributes);
      return cart;
    } catch (error) {
      console.error('No se pudieron limpiar los atributos fiscales del carrito:', error);
      return null;
    }
  }

  showSaveIndicator(type, message) {
    if (!this.saveIndicator || !this.saveIndicatorText) return;

    this.saveIndicatorText.textContent = message;
    this.saveIndicator.className = `save-indicator ${type} show`;

    clearTimeout(this._indicatorTimeout);
    this._indicatorTimeout = setTimeout(() => {
      this.saveIndicator.classList.remove('show');
    }, 2500);
  }

  handleCartFormSubmit(event) {
    if (this.isSubmitting) return;

    const submitter = event.submitter;

    if (!submitter || submitter.name !== 'checkout' || submitter.id !== 'update_cart_button') {
      return;
    }

    if (!this.isFormValid()) {
      event.preventDefault();
      this.showSaveIndicator('error', 'Completa los datos requeridos');
      return;
    }

    this.injectCartAttributes();
    this.isSubmitting = true;
    this.showSaveIndicator('success', '✓ Datos enviados al checkout');
  }
}

document.addEventListener('DOMContentLoaded', () => {
  window.billingForm = new BillingForm({
    checkoutButtonSelector: '#update_cart_button'
  });
});
